import { useCallback, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

export interface Feedback {
  tipo: 'success' | 'error';
  msg: string;
}

/**
 * Aviso de resultado das ações do backoffice.
 *
 * O gestor nem sempre sabia se o que fez pegou: quatro telas confirmavam com
 * Snackbar, cinco só tinham alerta embutido — que num formulário longo fica
 * fora da área visível — e uma usava `window.alert`. Aqui é o mesmo aviso em
 * todas: canto inferior, some sozinho, e o erro fica até o gestor fechar,
 * porque erro que some sozinho é erro que ninguém leu.
 */
export function useFeedback(): {
  feedback: Feedback | null;
  mostrarSucesso: (msg: string) => void;
  mostrarErro: (msg: string) => void;
  fecharFeedback: () => void;
} {
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  return {
    feedback,
    mostrarSucesso: useCallback((msg: string) => setFeedback({ tipo: 'success', msg }), []),
    mostrarErro: useCallback((msg: string) => setFeedback({ tipo: 'error', msg }), []),
    fecharFeedback: useCallback(() => setFeedback(null), []),
  };
}

export function SnackbarFeedback({
  feedback,
  aoFechar,
}: {
  feedback: Feedback | null;
  aoFechar: () => void;
}): JSX.Element {
  const ehErro = feedback?.tipo === 'error';

  return (
    <Snackbar
      open={feedback !== null}
      // Erro não some sozinho: o gestor precisa poder ler e agir.
      autoHideDuration={ehErro ? null : 4000}
      onClose={aoFechar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity={feedback?.tipo ?? 'success'} variant="filled" onClose={aoFechar}>
        {feedback?.msg}
      </Alert>
    </Snackbar>
  );
}
